/**
 * The file tree, derived.
 *
 * Nothing in Lattice stores "the current list of files". We fold the change log
 * (change.ts) to get it. That guarantees the tree and the provenance can never
 * disagree — there is only one source of truth — and it makes every question
 * about the tree a pure function we can test with a list of literals.
 *
 * DATA DEFINITIONS
 *
 *   A FileEntry is a structure { path, sha, size, updatedAt, updatedBy,
 *   createdAt, createdBy, revisions, lastMessage }.
 *   Interpretation: a file that EXISTS right now, together with the provenance
 *   summary the UI shows without needing to re-read the log. `revisions` counts
 *   how many times its content has changed, so the UI can show "rev 4".
 *
 *   A FileSet is a Map from path-key (see path.ts `pathKey`) to FileEntry.
 *   Interpretation: every file that exists in one project at one moment. Keyed
 *   case-insensitively because the engineers' Windows machines are.
 *
 *   A TreeNode is one of:
 *     - { type: "file",   ... }  a leaf
 *     - { type: "folder", ... }  a node with children
 *   Interpretation: the shape the UI renders. Folders are implied by the paths
 *   of the files inside them; an empty folder cannot exist, exactly as in git.
 */
import { pathKey, basename, dirname, ancestorsOf, comparePaths, compareNames, extensionOf } from './path.js';
import { bySeq } from './change.js';
import { isAfter } from './instant.js';
import {} from './user.js';
/**
 * Fold a change log into the set of files that exist now.
 *
 * The log MUST be in seq order; we sort defensively because callers assemble
 * it from several sources (imported history plus live changes).
 *
 * projectFiles : [Change] -> FileSet
 */
export function projectFiles(changes) {
    const files = new Map();
    for (const c of [...changes].sort(bySeq))
        applyChange(files, c);
    return files;
}
/**
 * Advance a FileSet by one Change, without disturbing the original.
 *
 * This is what an incrementally-updated cache needs: given what exists now and
 * one new fact, what exists next. `projectFiles` is the same operation folded
 * from nothing, so the two can never disagree about what a Change means.
 *
 * applyChangeTo : FileSet Change -> FileSet
 */
export function applyChangeTo(files, change) {
    const next = new Map(files);
    applyChange(next, change);
    return next;
}
/**
 * Advance a FileSet by many Changes, in sequence order.
 * applyChangesTo : FileSet [Change] -> FileSet
 */
export function applyChangesTo(files, changes) {
    const next = new Map(files);
    for (const c of [...changes].sort(bySeq))
        applyChange(next, c);
    return next;
}
/**
 * Apply one Change to a mutable working set.
 *
 * This is the only place that mutates, and it is private: callers see the pure
 * `projectFiles` / `applyChangeTo`. Keeping the mutation local is what makes
 * folding 800+ changes fast enough to do on every sync without a spinner.
 * applyChange : Map Change -> void
 */
function applyChange(files, c) {
    const key = pathKey(c.path);
    switch (c.kind) {
        case 'deleted':
            files.delete(key);
            return;
        case 'renamed': {
            const from = c.prevPath ? pathKey(c.prevPath) : undefined;
            const existing = from ? files.get(from) : undefined;
            if (from)
                files.delete(from);
            files.set(key, {
                // A rename preserves content and creation provenance; only the path
                // and the "who touched it last" fields move.
                path: c.path,
                sha: c.sha ?? existing?.sha ?? '',
                size: c.size ?? existing?.size ?? 0,
                createdAt: existing?.createdAt ?? c.at,
                createdBy: existing?.createdBy ?? c.authorId,
                updatedAt: c.at,
                updatedBy: c.authorId,
                revisions: existing?.revisions ?? 1,
                lastMessage: c.message,
            });
            return;
        }
        case 'added':
        case 'modified': {
            const existing = files.get(key);
            files.set(key, {
                path: c.path,
                sha: c.sha,
                size: c.size ?? 0,
                // Re-adding a previously deleted path starts a fresh history; that is
                // what "added" means. Only "modified" inherits the original creation.
                createdAt: c.kind === 'modified' && existing ? existing.createdAt : c.at,
                createdBy: c.kind === 'modified' && existing ? existing.createdBy : c.authorId,
                updatedAt: c.at,
                updatedBy: c.authorId,
                revisions: existing ? existing.revisions + 1 : 1,
                lastMessage: c.message,
            });
            return;
        }
    }
}
/**
 * Build the folder tree implied by a FileSet.
 *
 * Folders are sorted before files, then by natural name order, which is what a
 * file browser does and what the engineers expect.
 * buildTree : FileSet -> [TreeNode]
 */
export function buildTree(files) {
    const childrenOf = new Map();
    const ensure = (p) => {
        const existing = childrenOf.get(p);
        if (existing)
            return existing;
        const fresh = [];
        childrenOf.set(p, fresh);
        return fresh;
    };
    ensure('');
    // Every folder that any file implies, so we can attach children bottom-up.
    const folderPaths = new Set();
    for (const entry of files.values())
        for (const a of ancestorsOf(entry.path))
            folderPaths.add(a);
    for (const entry of [...files.values()].sort((a, b) => comparePaths(a.path, b.path))) {
        ensure(dirname(entry.path) ?? '').push({
            type: 'file', path: entry.path, name: basename(entry.path), entry,
        });
    }
    // Deepest folders first, so a folder's children exist before we fold it.
    const deepestFirst = [...folderPaths].sort((a, b) => b.split('/').length - a.split('/').length);
    for (const folder of deepestFirst) {
        const kids = ensure(folder);
        ensure(dirname(folder) ?? '').push(makeFolder(folder, kids));
    }
    return sortNodes(ensure(''));
}
/** Assemble a FolderNode from its children, rolling up counts and sizes. */
function makeFolder(path, children) {
    const sorted = sortNodes(children);
    let fileCount = 0;
    let totalSize = 0;
    let updatedAt = '';
    for (const child of sorted) {
        if (child.type === 'file') {
            fileCount += 1;
            totalSize += child.entry.size;
            if (updatedAt === '' || isAfter(child.entry.updatedAt, updatedAt))
                updatedAt = child.entry.updatedAt;
        }
        else {
            fileCount += child.fileCount;
            totalSize += child.totalSize;
            if (updatedAt === '' || isAfter(child.updatedAt, updatedAt))
                updatedAt = child.updatedAt;
        }
    }
    return { type: 'folder', path, name: basename(path), children: sorted, fileCount, totalSize, updatedAt };
}
/** Folders first, then natural name order. sortNodes : [TreeNode] -> [TreeNode] */
export function sortNodes(nodes) {
    return [...nodes].sort((a, b) => {
        if (a.type !== b.type)
            return a.type === 'folder' ? -1 : 1;
        return compareNames(a.name, b.name);
    });
}
/**
 * Every node in a tree, depth-first, parents before children.
 * Used to build the flat, virtualised list the UI actually renders.
 * flattenTree : [TreeNode] -> [TreeNode]
 */
export function flattenTree(nodes) {
    const out = [];
    const walk = (ns) => {
        for (const n of ns) {
            out.push(n);
            if (n.type === 'folder')
                walk(n.children);
        }
    };
    walk(nodes);
    return out;
}
export function visibleRows(nodes, expanded) {
    const out = [];
    const walk = (ns, depth) => {
        for (const node of ns) {
            out.push({ node, depth });
            if (node.type === 'folder' && expanded.has(node.path))
                walk(node.children, depth + 1);
        }
    };
    walk(nodes, 0);
    return out;
}
/** Find one node by exact path. findNode : [TreeNode] Path -> TreeNode | undefined */
export function findNode(nodes, path) {
    for (const n of nodes) {
        if (n.path === path)
            return n;
        if (n.type === 'folder' && path.startsWith(`${n.path}/`)) {
            const hit = findNode(n.children, path);
            if (hit)
                return hit;
        }
    }
    return undefined;
}
/** Every file at or below a node. filesUnder : TreeNode -> [FileEntry] */
export function filesUnder(node) {
    if (node.type === 'file')
        return [node.entry];
    return node.children.flatMap(filesUnder);
}
/**
 * Count files by extension across a FileSet — powers the project overview
 * ("47 parts, 46 drawings, 10 STEP").
 * countByExtension : FileSet -> Map<String, Number>
 */
export function countByExtension(files) {
    const counts = new Map();
    for (const f of files.values()) {
        const ext = extensionOf(f.path);
        counts.set(ext, (counts.get(ext) ?? 0) + 1);
    }
    return counts;
}
//# sourceMappingURL=tree.js.map